import PropTypes from 'prop-types';
import React from 'react';
import { Text, Clipboard, StyleSheet, TouchableWithoutFeedback, View } from 'react-native';

import MessageText from './MessageText';
import MessageImage, { IMAGE_WIDTH } from './MessageImage';
import MessageVideo from './MessageVideo';
import MessageGift from './MessageGift';

import Color from './Color';

import { isSameUser, isSameTime, isSameType } from './utils';
import { COLOR, FONT, FONT_SIZE, THEME, __WEB__ } from '../../../constants/styles';
import { DateUtils, ClipboardTouchable, I18n } from '../../components';

export default class Bubble extends React.Component {
  handleBubbleToNext() {
    if (
      isSameUser(this.props.currentMessage, this.props.nextMessage) &&
      isSameTime(this.props.currentMessage, this.props.nextMessage)
    ) {
      return styles[this.props.position].containerToNext;
    }
    return null;
  }

  handleBubbleToPrevious() {
    if (
      isSameUser(this.props.currentMessage, this.props.previousMessage) &&
      isSameTime(this.props.currentMessage, this.props.previousMessage) &&
      isSameType(this.props.currentMessage, this.props.previousMessage)
    ) {
      return styles[this.props.position].containerToPrevious;
    }
    return null;
  }

  handleImageBubbleStyle() {
    if (
      this.props.currentMessage.image ||
      this.props.currentMessage.imagePlaceholder ||
      this.props.currentMessage.video
    ) {
      return styles[this.props.position].wrapperImage;
    }
    return null;
  }

  renderMessageText() {
    // console.info('renderMessageText', this.props.currentMessage.text);
    const { ...messageTextProps } = this.props;
    const { currentMessage, directChatMode } = this.props;
    const isCaption = !!currentMessage.image || !!currentMessage.imagePlaceholder;

    const isBroadcast = currentMessage.broadcast;

    if (currentMessage.text) {
      return (
        <View style={[isCaption && { width: IMAGE_WIDTH }]}>
          {currentMessage.gift ? (
            <MessageGift {...messageTextProps} />
          ) : (
            <MessageText isBroadcast={isBroadcast} isCaption={isCaption} {...messageTextProps} />
          )}
        </View>
      );
    }
    return null;
  }

  renderMessageImage() {
    if (this.props.currentMessage.image || this.props.currentMessage.imagePlaceholder) {
      const { ...messageImageProps } = this.props;
      return (
        <View style={{ width: IMAGE_WIDTH }}>
          <MessageImage {...messageImageProps} />
        </View>
      );
    }
    return null;
  }

  renderMessageVideo() {
    if (this.props.currentMessage.video) {
      const { ...messageVideoProps } = this.props;
      return (
        <View style={{ width: IMAGE_WIDTH }}>
          <MessageVideo {...messageVideoProps} />
        </View>
      );
    }
    return null;
  }

  renderTicks() {
    const { currentMessage } = this.props;
    if (currentMessage.user._id !== this.props.user._id) {
      return null;
    }
    if (currentMessage.sent || currentMessage.received || currentMessage.pending) {
      return (
        <View style={styles.tickView}>
          {currentMessage.sent && <Text style={[styles.tick, this.props.tickStyle]}>✓</Text>}
          {currentMessage.received && <Text style={[styles.tick, this.props.tickStyle]}>✓</Text>}
          {currentMessage.pending && <Text style={[styles.tick, this.props.tickStyle]}>🕓</Text>}
        </View>
      );
    }
    return null;
  }

  renderBubbleHeader() {
    const { currentMessage, position } = this.props;

    const prev = this.handleBubbleToPrevious();
    if (prev) return null;

    const isMe = this._isMe();
    return (
      <View style={[styles.bubbleHeader, styles[position].bubbleHeader]}>
        <View style={{ flex: 1, flexDirection: 'row' }}>
          <Text style={styles.nameText} ellipsizeMode="tail" numberOfLines={1}>
            {isMe ? I18n.t('messaging.you') : currentMessage.user.name}
          </Text>
          <Text style={styles.bubbleTimeLabel}>
            {DateUtils.formatTimeString(currentMessage.createdAt)}
          </Text>
          {this.props.renderNameAccessoryView && this.props.renderNameAccessoryView(currentMessage)}
          {this.props.renderNameAccessoryView2 &&
            this.props.renderNameAccessoryView2(currentMessage)}
        </View>
      </View>
    );
  }

  _isMe = () => {
    return this.props.currentMessage?.user._id === this.props.user?._id;
  };

  _handleDeletePress = () => {
    this.props.onDeletePress && this.props.onDeletePress(this.props.currentMessage);
  };

  _longPressOptions = () => {
    const isMe = this._isMe();
    if (!isMe) return null;
    return [
      {
        label: I18n.t('messaging.deleteMessage'),
        action: this._handleDeletePress,
      },
    ];
  };

  render() {
    let { position, currentMessage, directChatMode } = this.props;
    const isBroadcast = currentMessage.broadcast;

    return (
      <View style={[styles.container, styles[position].container]}>
        {this.renderBubbleHeader()}
        <View
          style={[
            styles.wrapper,
            styles[position].wrapper,
            isBroadcast ? styles.broadcastWrapper : null,
            this.handleBubbleToNext(),
            this.handleBubbleToPrevious(),
            this.handleImageBubbleStyle(),
          ]}
        >
          <ClipboardTouchable
            hoverButtonsStyle={{ top: 8, right: 8 }}
            text={currentMessage.text}
            options={this._longPressOptions()}
          >
            <View>
              {this.renderMessageImage()}
              {this.renderMessageVideo()}
              {this.renderMessageText()}
            </View>
          </ClipboardTouchable>
        </View>
      </View>
    );
  }
}

const styles = {
  container: {
    flex: 1,
    alignItems: 'stretch',
    alignSelf: 'stretch',
  },
  bubbleHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'stretch',
    marginTop: 15,
    marginBottom: 5,
  },
  wrapper: {
    borderWidth: 1,
    borderRadius: 15,
    marginRight: 20,
    minHeight: 20,
    justifyContent: 'flex-end',
    borderBottomLeftRadius: __WEB__ ? 3 : 15,
    overflow: 'hidden',
  },
  left: StyleSheet.create({
    container: {},
    bubbleHeader: {},
    wrapperImage: {
      alignSelf: 'flex-start',
    },
    wrapper: {
      borderColor: '#777777',
      backgroundColor: '#F1F1F1',
    },
    containerToNext: {
      borderBottomLeftRadius: 15,
    },
    containerToPrevious: {},
    bottom: {
      flexDirection: 'row',
      justifyContent: 'flex-start',
    },
  }),
  right: StyleSheet.create({
    container: {},
    bubbleHeader: {},
    bubbleHeaderImageType: {},
    wrapperImage: {
      alignSelf: 'flex-start',
    },
    wrapper: {
      borderColor: '#777777',
      backgroundColor: '#2C426B',
    },
    containerToNext: {
      borderBottomLeftRadius: 15,
    },
    containerToPrevious: {},
    bottom: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
    },
  }),
  tick: {
    fontSize: 10,
    backgroundColor: Color.backgroundTransparent,
    color: Color.white,
  },
  tickView: {
    flexDirection: 'row',
    marginRight: 10,
  },
  bubbleTimeLabel: {
    ...FONT.medium,
    fontSize: FONT_SIZE.tiny,
    color: COLOR.textLight,
    marginLeft: 10,
    marginTop: 3,
  },
  nameText: {
    ...THEME.textSemibold,
  },
  broadcastWrapper: {
    backgroundColor: '#08c580',
  },
};

Bubble.defaultProps = {
  touchableProps: {},
  onLongPress: null,
  position: 'left',
  renderUsernameOnMessage: true,
  currentMessage: {
    text: null,
    createdAt: null,
    image: null,
  },
  nextMessage: {},
  previousMessage: {},
};
